import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { CheckIcon } from '@phosphor-icons/react';
import { ActionBar } from '~/components/ui/action-bar';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { CanvasMarkdown } from './canvas-markdown';
import { documentKeys, useDocumentDetail } from '~/hooks/queries/use-documents';
import { fileKeys, useFileDetail, useUpdateFile } from '~/hooks/queries/use-files';
import { useUpdateDocument } from '~/hooks/mutations/use-document-mutations';
import { api, ApiError } from '~/lib/api-client';
import {
  clearCanvasDraft,
  loadCanvasDraft,
  saveCanvasDraft,
  type CanvasDraft,
  type CanvasView,
} from './canvas-storage';
import { decideSave } from './save-decision';
import {
  adoptPendingBase,
  baseForEdit,
  baseForWrite,
  documentBranch,
  draftAfterSave,
  isDraftDirty,
  isStaleSaveRefusal,
  saveBlockReason,
  showConflictNotice,
} from './canvas-editor-logic';
import { createDebouncedWriter } from './debounced-writer';
import { ChangesView } from './changes-view';
import { NO_DOCUMENT_CHANGES, useDocumentChanges, type DocumentChanges } from './use-document-changes';
import type { CanvasFlush, CanvasFlushResult } from './canvas-send-gate';

/** Trailing delay for the draft's localStorage write; flushed on unmount, pagehide, save and switch. */
const DRAFT_WRITE_MS = 300;

/** The two fields every text source (a document, a Markdown file) must give the editor. */
interface TextDetail {
  content: string;
  updatedAt: string;
}

/**
 * Where a text item's words come from and go to. A document and a Markdown FILE
 * share the whole viewer and editor; only these four things differ.
 */
interface TextSource {
  /** `document:<filename>` | `file:<id>` — also the draft key. */
  itemKey: string;
  /** "document" | "file": the word used in the person-facing messages. */
  noun: string;
  /** The detail query's cache key, so a fresh read and a save write the same entry. */
  detailKey: readonly unknown[];
  /** The route a fresh read of this one item goes to (the stale check). */
  detailPath: string;
  /**
   * Write the new text; resolves once the server holds it. `baseUpdatedAt` is the
   * version the save is checked against, for a source whose PUT can refuse a
   * stale one (documents); a source whose PUT cannot (Markdown files) ignores it.
   */
  write: (content: string, baseUpdatedAt: string) => Promise<unknown>;
}

export type CanvasSaveStatus = 'idle' | 'saving' | 'saved';

interface DocumentCanvasProps {
  sessionId: string;
  filename: string;
  view: CanvasView;
  onStatusChange: (status: CanvasSaveStatus) => void;
  onDirtyChange: (dirty: boolean) => void;
  /** Where the mounted editor registers its save-before-send flush (see canvas-send-gate.ts). */
  flushRef?: MutableRefObject<CanvasFlush | null>;
}

/** A memory document: read through the documents route, saved through the documents PUT. */
export function DocumentCanvas({ sessionId, filename, view, onStatusChange, onDirtyChange, flushRef }: DocumentCanvasProps) {
  const query = useDocumentDetail(filename);
  const changes = useDocumentChanges(filename);
  const update = useUpdateDocument();
  const source: TextSource = {
    itemKey: `document:${filename}`,
    noun: 'document',
    detailKey: documentKeys.detail(filename),
    detailPath: `/api/documents/${encodeURIComponent(filename)}`,
    write: (content, baseUpdatedAt) => update.mutateAsync({ filename, content, baseUpdatedAt }),
  };
  return (
    <TextCanvas
      sessionId={sessionId}
      source={source}
      query={query}
      changes={changes}
      view={view}
      onStatusChange={onStatusChange}
      onDirtyChange={onDirtyChange}
      flushRef={flushRef}
    />
  );
}

/** A Markdown FILE (a Files upload / plain `file_write`): read through the files detail route, saved through the files PUT. */
export function FileCanvas({
  sessionId,
  fileId,
  view,
  onStatusChange,
  onDirtyChange,
  flushRef,
}: Omit<DocumentCanvasProps, 'filename'> & { fileId: string }) {
  const query = useFileDetail(fileId);
  const update = useUpdateFile();
  const source: TextSource = {
    itemKey: `file:${fileId}`,
    noun: 'file',
    detailKey: fileKeys.detail(fileId),
    detailPath: `/api/files/${encodeURIComponent(fileId)}`,
    // Content only: the PUT is partial, so the file's name is never echoed back.
    // The files PUT has no version check (no `baseUpdatedAt` on main), so the
    // client pre-check stays the only guard for a file.
    write: (content) => update.mutateAsync({ id: fileId, content }),
  };
  return (
    <TextCanvas
      sessionId={sessionId}
      source={source}
      query={query}
      view={view}
      onStatusChange={onStatusChange}
      onDirtyChange={onDirtyChange}
      flushRef={flushRef}
    />
  );
}

interface TextCanvasProps {
  sessionId: string;
  source: TextSource;
  query: UseQueryResult<TextDetail, Error>;
  /** The Changes view's data; a Markdown file has no version history and omits it. */
  changes?: DocumentChanges;
  view: CanvasView;
  onStatusChange: (status: CanvasSaveStatus) => void;
  onDirtyChange: (dirty: boolean) => void;
  flushRef?: MutableRefObject<CanvasFlush | null>;
}

/** Loading / error / viewers for one text item. Each branch keys off the query's own flags. */
function TextCanvas({ sessionId, source, query, changes = NO_DOCUMENT_CHANGES, view, onStatusChange, onDirtyChange, flushRef }: TextCanvasProps) {
  const { itemKey, noun } = source;
  const status = query.error instanceof ApiError ? query.error.status : null;
  const stored = query.isError && status === 404 ? loadCanvasDraft(sessionId, itemKey) : null;
  const branch = documentBranch({
    isPending: query.isPending,
    isError: query.isError,
    status,
    hasData: !!query.data,
    view,
    hasStoredDraft: !!stored && stored.content !== query.data?.content,
  });

  if (branch === 'loading') {
    return (
      <div className="p-6 space-y-3" data-testid="canvas-document-loading">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }
  if (branch === 'error') {
    return (
      <div role="alert" data-testid="canvas-document-error" className="p-6 text-sm text-muted-foreground space-y-3">
        <p>Couldn't load this {noun}.</p>
        <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (branch === 'gone') {
    return (
      <div role="alert" data-testid="canvas-document-error" className="p-6 text-sm text-muted-foreground">
        <p>This {noun} no longer exists.</p>
      </div>
    );
  }
  if (branch === 'gone-draft') return <GoneText text={stored!.content} noun={noun} />;

  const doc = query.data!;
  const note =
    branch === 'content-stale' ? (
      <p
        role="status"
        data-testid="canvas-refresh-failed"
        className="shrink-0 border-b border-border bg-muted px-4 py-2 text-xs text-muted-foreground"
      >
        Couldn't refresh this {noun}. Showing the last version loaded.{' '}
        <button type="button" className="underline" onClick={() => void query.refetch()}>
          Retry
        </button>
      </p>
    ) : null;
  let body;
  if (view === 'edit') {
    body = (
      <TextEditor
        key={`${sessionId}:${itemKey}`}
        sessionId={sessionId}
        source={source}
        serverContent={doc.content}
        serverUpdatedAt={doc.updatedAt}
        gone={branch === 'gone-editor'}
        onStatusChange={onStatusChange}
        onDirtyChange={onDirtyChange}
        flushRef={flushRef}
      />
    );
  } else if (view === 'changes') {
    // Not the editor, exactly like Preview: an unsaved Edit draft is flushed to
    // storage when the editor unmounts and restored when Edit is chosen again.
    body = (
      <ChangesView
        availability={changes.availability}
        data={changes.data}
        onRetry={changes.refetch}
        onRestore={changes.restorePrevious.run}
        restoring={changes.restorePrevious.pending}
      />
    );
  } else {
    body = (
      <div className="p-6" data-testid="canvas-document-preview">
        <CanvasMarkdown content={doc.content} />
      </div>
    );
  }
  return frameDocument(note, body);
}

/**
 * The one frame every content branch returns. The note slot is ALWAYS there
 * (null when there is nothing to say) and the body sits at a fixed child index,
 * so React keeps the same editor instance when the "Couldn't refresh" note
 * appears and disappears. Returning the bare body in one case and a wrapper in
 * the other would move the editor to a different tree position and remount it,
 * throwing away its in-memory draft, base and in-flight save.
 */
export function frameDocument(note: ReactNode, body: ReactNode) {
  return (
    <div data-testid="canvas-document-frame" className="flex min-h-0 flex-1 flex-col">
      {note}
      {body}
    </div>
  );
}

/** "Copy text" for words that would otherwise be lost with a deleted document. */
function CopyTextButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-testid="canvas-copy-text"
      onClick={() => {
        void navigator.clipboard
          ?.writeText(text)
          .then(() => setCopied(true))
          .catch(() => {});
      }}
    >
      {copied ? 'Copied' : 'Copy text'}
    </Button>
  );
}

/** The document is gone but the person's unsaved words are not: shown read-only, copyable. */
function GoneText({ text, noun }: { text: string; noun: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4" data-testid="canvas-gone-draft">
      <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <p>This {noun} no longer exists. Your unsaved text is kept below.</p>
        <CopyTextButton text={text} />
      </div>
      <Textarea
        data-testid="canvas-gone-text"
        aria-label="Your unsaved text"
        readOnly
        value={text}
        className="min-h-48 flex-1 resize-none font-mono text-[length:var(--text-sm)]"
      />
    </div>
  );
}

function TextEditor({
  sessionId,
  source,
  serverContent,
  serverUpdatedAt,
  gone,
  onStatusChange,
  onDirtyChange,
  flushRef,
}: {
  sessionId: string;
  source: TextSource;
  serverContent: string;
  serverUpdatedAt: string;
  /** The item was deleted elsewhere: read-only, nothing can be saved, the text can be copied. */
  gone: boolean;
  onStatusChange: (status: CanvasSaveStatus) => void;
  onDirtyChange: (dirty: boolean) => void;
  flushRef?: MutableRefObject<CanvasFlush | null>;
}) {
  const queryClient = useQueryClient();
  const { itemKey, noun, detailKey, detailPath } = source;
  const [draft, setDraft] = useState<CanvasDraft | null>(() => loadCanvasDraft(sessionId, itemKey));
  const [saveConflict, setSaveConflict] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Set when a save succeeded but reading the row back failed: the cached row is
  // the one from BEFORE the save, so a draft started now must not take its base
  // from it. It ends when a refetched row (a different updatedAt) lands.
  const [stalePastSave, setStalePastSave] = useState<string | null>(null);
  const basePending = stalePastSave !== null && stalePastSave === serverUpdatedAt;
  // What the box shows right now, readable from an async save that outlives the
  // render it started in (the person keeps typing while the request is in flight).
  const latestTextRef = useRef<string>(draft?.content ?? serverContent);
  const [writer] = useState(() =>
    createDebouncedWriter<CanvasDraft>((d) => saveCanvasDraft(sessionId, itemKey, d), DRAFT_WRITE_MS),
  );

  const content = draft?.content ?? serverContent;
  const dirty = isDraftDirty(draft, serverContent);
  const conflict = !gone && showConflictNotice({ draft, serverContent, serverUpdatedAt, saveConflict });
  const blockReason = gone ? `This ${noun} no longer exists.` : saveBlockReason({ content, conflict });

  // The refetched row has landed: a draft still waiting for its base takes it.
  useEffect(() => {
    const adopted = adoptPendingBase({ draft, serverUpdatedAt, basePending });
    if (stalePastSave !== null && !basePending) setStalePastSave(null);
    if (!adopted) return;
    saveCanvasDraft(sessionId, itemKey, adopted);
    setDraft(adopted);
  }, [draft, serverUpdatedAt, basePending, stalePastSave, sessionId, itemKey]);

  useEffect(() => {
    onDirtyChange(dirty && !gone);
    return () => onDirtyChange(false);
  }, [dirty, gone, onDirtyChange]);

  // Flush the debounced draft write when the editor goes away (switching item or
  // view) and when the page is hidden, so a pending keystroke is never lost.
  useEffect(() => {
    const onHide = () => writer.flush();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      writer.flush();
    };
  }, [writer]);

  function discard() {
    writer.cancel();
    clearCanvasDraft(sessionId, itemKey);
    setDraft(null);
    latestTextRef.current = serverContent;
    setSaveConflict(false);
    setSaveError(null);
  }

  function onType(value: string) {
    latestTextRef.current = value;
    const next: CanvasDraft = { content: value, baseUpdatedAt: baseForEdit({ draft, serverUpdatedAt, basePending }) };
    // Typing the text back to what the server holds is no edit: drop the draft.
    if (value === serverContent) {
      discard();
      latestTextRef.current = value;
    } else {
      writer.schedule(next);
      setDraft(next);
    }
    setSaveError(null);
    onStatusChange('idle');
  }

  /** The server's copy of this document right now, also written into the detail cache. */
  async function fetchFresh(): Promise<TextDetail> {
    const fresh = await api.get<TextDetail>(detailPath);
    queryClient.setQueryData(detailKey, fresh);
    return fresh;
  }

  /**
   * After a successful PUT: adopt the real row (its new updatedAt). If that
   * re-read fails, show the sent text, invalidate the detail query so it is read
   * again, and return null — the caller then marks the base as pending.
   */
  async function adoptSaved(sent: string): Promise<string | null> {
    try {
      return (await fetchFresh()).updatedAt;
    } catch {
      queryClient.setQueryData(detailKey, (old: TextDetail | undefined) => (old ? { ...old, content: sent } : old));
      void queryClient.invalidateQueries({ queryKey: detailKey });
      return null;
    }
  }

  // The save running right now, if any. A ref (not the `saving` state) so a second
  // trigger — the Save button, "Keep mine", or a chat send's flush — joins the
  // running save instead of starting another PUT in the gap before a re-render.
  const inFlightRef = useRef<Promise<CanvasFlushResult> | null>(null);

  /** Save the draft. Resolves how it ended; never rejects. */
  function save(force: boolean): Promise<CanvasFlushResult> {
    if (inFlightRef.current) return inFlightRef.current;
    const run = runSave(force).finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = run;
    return run;
  }

  async function runSave(force: boolean): Promise<CanvasFlushResult> {
    if (!draft || gone) return 'clean';
    const sent = draft.content;
    setSaving(true);
    setSaveError(null);
    onStatusChange('saving');
    try {
      let currentUpdatedAt = serverUpdatedAt;
      if (!force) {
        // Ask the server what it holds NOW first, and keep that copy in the
        // detail cache so "Load Eddy's version" shows it. The PUT's own version
        // check below closes the gap that remains after this read.
        const fresh = await fetchFresh();
        const decision = decideSave({
          draft: sent,
          baseUpdatedAt: draft.baseUpdatedAt,
          serverUpdatedAt: fresh.updatedAt,
        });
        if (decision !== 'save') {
          if (decision === 'conflict') setSaveConflict(true);
          onStatusChange('idle');
          return decision === 'conflict' ? 'conflict' : 'failed';
        }
        currentUpdatedAt = fresh.updatedAt;
      }
      await source.write(
        sent,
        baseForWrite({ force, draftBase: draft.baseUpdatedAt, currentUpdatedAt }),
      );
      const savedAt = await adoptSaved(sent);
      // The re-read failed: the cache still holds the pre-save row until the
      // invalidation's refetch lands, so remember which row is the stale one.
      if (savedAt === null) setStalePastSave(serverUpdatedAt);
      const keep = draftAfterSave({ sent, latestText: latestTextRef.current, savedUpdatedAt: savedAt });
      if (keep === null) {
        discard();
      } else {
        // Typed while the save was in flight: keep it, based on the saved version.
        writer.cancel();
        saveCanvasDraft(sessionId, itemKey, keep);
        setDraft(keep);
        setSaveConflict(false);
      }
      onStatusChange('saved');
      return 'saved';
    } catch (err) {
      if (isStaleSaveRefusal(err)) {
        // FRONT-2600: the version check refused the write — the same notice as the
        // pre-check, with the server's current copy put in the cache so "Load
        // Eddy's version" shows it. The draft is untouched.
        try {
          await fetchFresh();
        } catch {
          void queryClient.invalidateQueries({ queryKey: detailKey });
        }
        setSaveConflict(true);
        onStatusChange('idle');
        return 'conflict';
      }
      setSaveError("Couldn't save. Your changes are kept, try again.");
      onStatusChange('idle');
      return 'failed';
    } finally {
      setSaving(false);
    }
  }

  /**
   * The save-before-send flush (canvas-send-gate.ts): the SAME version-checked
   * save the Save button runs. Clean -> 'clean'; a notice already on screen or an
   * unsavable draft -> held without a request; otherwise save and report how it
   * ended. A save already running is awaited, then the (now re-rendered) editor is
   * asked once more, so keystrokes typed during it are not sent past.
   */
  async function flushDirtySave(): Promise<CanvasFlushResult> {
    if (inFlightRef.current) {
      await inFlightRef.current;
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      return (await flushRef?.current?.()) ?? 'clean';
    }
    if (gone || !isDraftDirty(draft, serverContent)) return 'clean';
    if (conflict) return 'conflict';
    if (blockReason) return 'failed';
    return save(false);
  }
  // Registered after every commit so the registered function always closes over the
  // latest draft; removed on unmount (only if it is still ours).
  useEffect(() => {
    if (!flushRef) return;
    flushRef.current = flushDirtySave;
    const mine = flushDirtySave;
    return () => {
      if (flushRef.current === mine) flushRef.current = null;
    };
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {gone && (
        <div
          role="alert"
          data-testid="canvas-gone-notice"
          className="shrink-0 flex flex-wrap items-center gap-3 border-b border-border bg-muted px-4 py-3 text-sm text-foreground"
        >
          <p>This {noun} no longer exists. Your text is kept here, read-only.</p>
          <CopyTextButton text={content} />
        </div>
      )}
      {conflict && (
        <div
          role="alert"
          data-testid="canvas-conflict"
          className="shrink-0 border-b border-border bg-muted px-4 py-3 text-sm text-foreground"
        >
          <p className="mb-2">EDDY changed this {noun} after you started editing.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => void save(true)}>
              Keep mine
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={discard}>
              Load EDDY's version
            </Button>
          </div>
        </div>
      )}
      <Textarea
        data-testid="canvas-editor"
        aria-label={`${noun === 'file' ? 'File' : 'Document'} text`}
        value={content}
        onChange={(e) => onType(e.target.value)}
        readOnly={gone}
        spellCheck
        className="min-h-48 flex-1 field-sizing-fixed resize-none rounded-none border-0 bg-transparent p-6 font-mono text-[length:var(--text-sm)] shadow-none focus-visible:ring-0 dark:bg-transparent"
      />
      {(saveError || (dirty && !gone && blockReason)) && (
        <p
          data-testid="canvas-save-reason"
          role={saveError ? 'alert' : undefined}
          className={`shrink-0 px-4 pb-2 text-xs ${saveError ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {saveError ?? blockReason}
        </p>
      )}
      <ActionBar
        dirty={dirty && !gone}
        saving={saving}
        saveDisabled={blockReason !== null}
        message="Unsaved changes"
        onSave={() => void save(false)}
        onDiscard={discard}
      />
    </div>
  );
}

/** The persistent "Saved" / "Saving…" marker shown in the panel header. */
export function CanvasSaveStatus({ status }: { status: CanvasSaveStatus }) {
  if (status === 'idle') return null;
  if (status === 'saving') {
    return (
      <span data-testid="canvas-save-status" data-status="saving" className="text-xs text-muted-foreground">
        Saving…
      </span>
    );
  }
  return (
    <span
      data-testid="canvas-save-status"
      data-status="saved"
      className="inline-flex items-center gap-1 text-xs text-success"
    >
      <CheckIcon size={12} aria-hidden />
      Saved
    </span>
  );
}
